import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { contactApi } from '../services';
import Icon from './Icon';
import './RelationshipManagers.css';
import './IcebreakersPanel.css';

// Approximate size of the contact list, for the summary line. Hardcoded, not fetched.
const APPROX_CONTACT_TOTAL = '5,297';

// education and work_experience_structured arrive as JSON text. Returns the entries (objects only) when
// the value is a list, or null when it isn't, so the caller can show it as written.
const parseList = (value) => {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (parsed === null) return [];
    return Array.isArray(parsed) ? parsed.filter(entry => entry && typeof entry === 'object') : null;
  } catch (err) {
    return null;
  }
};

// The heading and muted detail line for one entry of each list field
const educationLine = (entry) => ({
  heading: entry.school || entry.institution,
  detail: [
    [entry.degree, entry.field_of_study].filter(Boolean).join(', '),
    entry.years || entry.dates || [entry.start_year, entry.end_year].filter(Boolean).join(' – ')
  ].filter(Boolean).join(' · ')
});

const roleLine = (entry) => ({
  heading: [entry.title, entry.company].filter(Boolean).join(' at '),
  detail: [
    [entry.start_date, entry.end_date || (entry.is_current ? 'Present' : null)].filter(Boolean).join(' – '),
    entry.duration,
    entry.location
  ].filter(Boolean).join(' · ')
});

const TextField = ({ label, value }) => (
  <p className="icebreaker-field">
    <span className="icebreaker-field__label">{label}:</span>{' '}
    <span className="icebreaker-field__value">{value}</span>
  </p>
);

const ListField = ({ label, value, toLine }) => {
  const entries = parseList(value);
  if (entries === null) {
    return <TextField label={label} value={typeof value === 'string' ? value : JSON.stringify(value)} />;
  }
  if (entries.length === 0) return null;
  return (
    <div className="icebreaker-field">
      <span className="icebreaker-field__label">{label}:</span>
      <ul className="icebreaker-entries">
        {entries.map((entry, index) => {
          const { heading, detail } = toLine(entry);
          return (
            <li key={index}>
              {heading}
              {detail && <span className="icebreaker-entry__detail">{detail}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// Each field shows only when the contact has it
const IcebreakerCard = ({ contact }) => {
  const subtitle = [contact.company_name, contact.title].filter(Boolean).join(' · ');
  return (
    <li className="icebreaker-card">
      <div className="icebreaker-card__name">{contact.name}</div>
      {subtitle && <div className="icebreaker-card__subtitle">{subtitle}</div>}
      {contact.headline && <TextField label="Headline" value={contact.headline} />}
      {contact.bio && <TextField label="Bio" value={contact.bio} />}
      {contact.summary && contact.summary !== contact.bio && <TextField label="Summary" value={contact.summary} />}
      {contact.education && <ListField label="Education" value={contact.education} toLine={educationLine} />}
      {contact.work_experience_structured && (
        <ListField label="Work history" value={contact.work_experience_structured} toLine={roleLine} />
      )}
    </li>
  );
};

/**
 * Contacts with any LinkedIn background on file (education, work history, bio, headline, summary), shown
 * as raw facts. Read-only, and nothing here is matched against anyone on the team.
 */
const IcebreakersPanel = ({ isOpen, onClose }) => {
  const [contacts, setContacts] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const dialogRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const result = await contactApi.getContactsWithBackground();
      setContacts(result.contacts || []);
    } catch (err) {
      setLoadError(err.message);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setContacts(null);
    setLoadError(null);
    load();
    dialogRef.current?.focus();
  }, [isOpen, load]);

  // Escape closes this dialog without reaching the panels underneath
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="rm-overlay" onClick={onClose} aria-hidden="true" />
      <div className="rm-dialog" role="dialog" aria-modal="true" aria-labelledby="icebreakers-dialog-title"
           ref={dialogRef} tabIndex={-1}>
        <header className="rm-dialog__header">
          <h2 id="icebreakers-dialog-title"><Icon name="graduation-cap" /> Icebreakers</h2>
          <button type="button" className="rm-icon-button" onClick={onClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </header>

        <div className="rm-dialog__body">
          {contacts && (
            <p className="icebreakers-summary">
              {contacts.length} of {APPROX_CONTACT_TOTAL} contacts have anything on file.
            </p>
          )}
          <p className="icebreakers-note">
            Raw facts only, not matched against anyone on the team - what to do with them is your call.
          </p>
          {loadError && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {loadError}</p>}
          {!contacts && !loadError && (
            <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>
          )}
          {contacts && contacts.length === 0 && (
            <p className="rm-empty">No contacts have any LinkedIn background on file yet. A LinkedIn PDF adds it.</p>
          )}
          <ul className="icebreakers-list">
            {contacts?.map(contact => <IcebreakerCard key={contact.id} contact={contact} />)}
          </ul>
        </div>
      </div>
    </>,
    document.body
  );
};

export default IcebreakersPanel;
